import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Trash2, User, Lock, Mail, Save, Pencil, ShoppingBag, AlertTriangle, Clock, CalendarDays, Users, GripVertical, Bell, Send, Languages, ClipboardCheck, MessageSquare } from "lucide-react";
import { cmsFields } from "./cms/cmsFieldsConfig";
import { translateFields } from "@/lib/cms-translate";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";

interface Zone {
  id: string;
  name: string;
  base_fee: number;
  is_active: boolean;
}

const AdminSettings = () => {
  const [zones, setZones] = useState<Zone[]>([]);
  const [newZone, setNewZone] = useState({ name: "", base_fee: 0 });
  const [editingZone, setEditingZone] = useState<string | null>(null);
  const [editValues, setEditValues] = useState<{ name: string; base_fee: number }>({ name: "", base_fee: 0 });
  const { user } = useAuth();
  const { toast } = useToast();

  // Profile fields
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingEmail, setSavingEmail] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);

  // Site translation
  const [translatingSite, setTranslatingSite] = useState(false);
  const [translateStatus, setTranslateStatus] = useState("");

  // Shop toggle
  const [shopEnabled, setShopEnabled] = useState(false);
  const [shopLoading, setShopLoading] = useState(false);
  const [certificatesEnabled, setCertificatesEnabled] = useState(true);
  const [certificatesLoading, setCertificatesLoading] = useState(false);
  // Data cleanup
  const [deletingBookings, setDeletingBookings] = useState(false);
  const [deletingOrders, setDeletingOrders] = useState(false);

  // Gender options
  const [genderStepEnabled, setGenderStepEnabled] = useState(true);
  const [genderOptions, setGenderOptions] = useState<Array<{id: string; label: string; sublabel?: string; icon: string; image_url?: string; action: string}>>([
    { id: "female", label: "Mulher", icon: "Heart", action: "select" },
    { id: "child", label: "Criança", sublabel: "(menina)", icon: "Flower2", action: "select" },
    { id: "male", label: "Homem", icon: "User", action: "select" },
    { id: "other", label: "Outro", icon: "Shield", action: "block" },
  ]);
  const [genderStepTitle, setGenderStepTitle] = useState("Informação do paciente");
  const [genderStepDescription, setGenderStepDescription] = useState("A Mente Sã é um espaço dedicado ao bem-estar feminino e infantil. Por favor, indique o perfil do paciente para que possamos oferecer o melhor atendimento.");
  const [savingGender, setSavingGender] = useState(false);

  const availableIcons = ["Heart", "Flower2", "User", "Shield", "Baby", "Sparkles", "Eye", "Hand"];
  const [bufferBefore, setBufferBefore] = useState(0);
  const [bufferAfter, setBufferAfter] = useState(0);
  const [allowSameDay, setAllowSameDay] = useState(false);
  const [minAdvanceHours, setMinAdvanceHours] = useState(3);
  const [savingBookingSettings, setSavingBookingSettings] = useState(false);

  // Reminder automation
  const defaultReminder = {
    enabled: false,
    send_time: "13:00",
    days_before: [1] as number[],
    window_minutes: 15,
    whatsapp_instances: [] as any[],
    client: {
      enabled: true,
      email: true,
      whatsapp: true,
      message_whatsapp: 'Olá {{nome}}!  Lembrete: tem uma sessão de "{{servico}}" marcada para {{data}} às {{hora}} com {{colaborador}}. Local: {{local}}. Até breve!, Mente Sã',
      email_subject: "Lembrete, Sessão a {{data}} às {{hora}}",
      email_body: "Olá {{nome}},\n\nEste é um lembrete carinhoso da sua sessão marcada:\n\n· Serviço: {{servico}}\n· Data: {{data}}\n· Hora: {{hora}}\n· Colaborador: {{colaborador}}\n· Local: {{local}}\n· Valor: {{valor}}\n\nSe precisar de reagendar, contacte-nos com antecedência.\n\nCom carinho,\nMente Sã",
    },
    staff: {
      enabled: true,
      email: false,
      whatsapp: true,
      whatsapp_instance: null as string | null,
      message_whatsapp: "Olá {{colaborador}}!  Lembrete: sessão marcada com {{nome}} ({{telefone}}), {{servico}} em {{data}} às {{hora}}. Local: {{local}}.",
      email_subject: "Lembrete, Sessão com {{nome}} a {{data}} às {{hora}}",
      email_body: "Olá {{colaborador}},\n\nLembrete de sessão agendada:\n\n· Cliente: {{nome}}\n· Telefone: {{telefone}}\n· Email: {{email_cliente}}\n· Serviço: {{servico}}\n· Data: {{data}}\n· Hora: {{hora}}\n· Local: {{local}}\n\nMente Sã",
    },
  };
  const [reminder, setReminder] = useState(defaultReminder);
  const [daysBeforeInput, setDaysBeforeInput] = useState("1");
  const [savingReminder, setSavingReminder] = useState(false);
  const [testingReminder, setTestingReminder] = useState(false);

  const fetchReminderSettings = async () => {
    const { data: appData } = await supabase.from("app_settings").select("key, value").in("key", ["reminder_settings", "whatsapp_instances"]);
    
    let loadedReminder = { ...defaultReminder };
    let instances: any[] = [];

    appData?.forEach(row => {
      if (row.key === "whatsapp_instances") {
        instances = Array.isArray(row.value) ? row.value : [];
      } else if (row.key === "reminder_settings" && row.value && typeof row.value === "object") {
        const v = row.value as any;
        loadedReminder = {
          ...defaultReminder,
          ...v,
          client: { ...defaultReminder.client, ...(v.client || {}) },
          staff: { ...defaultReminder.staff, ...(v.staff || {}) },
          days_before: Array.isArray(v.days_before) && v.days_before.length ? v.days_before : defaultReminder.days_before,
        };
      }
    });

    setReminder({ ...loadedReminder, whatsapp_instances: instances });
    if (loadedReminder.days_before) {
      setDaysBeforeInput(loadedReminder.days_before.join(","));
    }
  };

  const saveReminderSettings = async () => {
    setSavingReminder(true);
    const days = daysBeforeInput
      .split(",")
      .map((s) => parseInt(s.trim(), 10))
      .filter((n) => Number.isFinite(n) && n > 0);
    const payload = { ...reminder, days_before: days.length ? days : [1] };
    // Remove transient field before saving to DB
    const { whatsapp_instances, ...savePayload } = payload;
    const { error } = await supabase.from("app_settings").upsert(
      { key: "reminder_settings", value: savePayload as any },
      { onConflict: "key" }
    );
    if (error) {
      toast({ title: "Erro ao guardar", description: error.message, variant: "destructive" });
    } else {
      setReminder(payload);
      toast({ title: "Lembretes automáticos guardados!" });
    }
    setSavingReminder(false);
  };

  const testReminderNow = async () => {
    setTestingReminder(true);
    try {
      const { data, error } = await supabase.functions.invoke("booking-reminders", {
        body: {},
        // ignored by function; force via URL requires curl. Fall back to just invoking.
      });
      if (error) throw error;
      toast({ title: "Teste executado", description: `Processados: ${data?.processed ?? 0}` });
    } catch (e: any) {
      toast({ title: "Erro no teste", description: e?.message || "Falhou", variant: "destructive" });
    }
    setTestingReminder(false);
  };



  const fetchZones = async () => {
    const { data } = await supabase.from("delivery_zones").select("*").order("name");
    setZones((data as Zone[]) || []);
  };

  const fetchShopEnabled = async () => {
    const { data } = await supabase.from("app_settings").select("value").eq("key", "shop_enabled").maybeSingle();
    if (data) {
      const val = typeof data.value === "string" ? data.value : JSON.stringify(data.value);
      setShopEnabled(val === "true" || val === '"true"');
    }
  };

  const fetchCertificatesEnabled = async () => {
    const { data } = await supabase.from("app_settings").select("value").eq("key", "certificates_enabled").maybeSingle();
    if (data) {
      const v: any = data.value;
      setCertificatesEnabled(v === true || v === "true" || v === '"true"');
    }
  };

  const fetchBookingSettings = async () => {
    const { data } = await supabase.from("app_settings").select("value").eq("key", "booking_settings").maybeSingle();
    if (data?.value && typeof data.value === "object") {
      const s = data.value as Record<string, any>;
      setBufferBefore(s.buffer_before ?? 0);
      setBufferAfter(s.buffer_after ?? 0);
      setAllowSameDay(s.allow_same_day ?? false);
      setMinAdvanceHours(s.min_advance_hours ?? 3);
    }
  };

  const saveBookingSettings = async () => {
    setSavingBookingSettings(true);
    const { error } = await supabase.from("app_settings").upsert({
      key: "booking_settings",
      value: {
        buffer_before: bufferBefore,
        buffer_after: bufferAfter,
        allow_same_day: allowSameDay,
        min_advance_hours: minAdvanceHours,
      },
    }, { onConflict: "key" });
    if (error) {
      toast({ title: "Erro ao guardar", variant: "destructive" });
    } else {
      toast({ title: "Definições de reserva guardadas!" });
    }
    setSavingBookingSettings(false);
  };

  const fetchGenderOptions = async () => {
    const { data } = await supabase.from("app_settings").select("key, value").in("key", ["gender_options", "gender_step_title", "gender_step_description", "gender_step_enabled"]);
    (data || []).forEach((row: any) => {
      if (row.key === "gender_options" && Array.isArray(row.value)) setGenderOptions(row.value);
      if (row.key === "gender_step_title" && typeof row.value === "string") setGenderStepTitle(row.value);
      if (row.key === "gender_step_description" && typeof row.value === "string") setGenderStepDescription(row.value);
      if (row.key === "gender_step_enabled") setGenderStepEnabled(row.value !== false && row.value !== "false");
    });
  };

  const saveGenderOptions = async () => {
    setSavingGender(true);
    await Promise.all([
      supabase.from("app_settings").upsert({ key: "gender_options", value: genderOptions as any }, { onConflict: "key" }),
      supabase.from("app_settings").upsert({ key: "gender_step_title", value: genderStepTitle as any }, { onConflict: "key" }),
      supabase.from("app_settings").upsert({ key: "gender_step_description", value: genderStepDescription as any }, { onConflict: "key" }),
      supabase.from("app_settings").upsert({ key: "gender_step_enabled", value: genderStepEnabled as any }, { onConflict: "key" }),
    ]);
    toast({ title: "Opções de perfil guardadas!" });
    setSavingGender(false);
  };

  const addGenderOption = () => {
    setGenderOptions([...genderOptions, { id: `option_${Date.now()}`, label: "Novo", icon: "User", action: "select" }]);
  };

  const updateGenderOption = (index: number, updates: Partial<typeof genderOptions[0]>) => {
    setGenderOptions(prev => prev.map((opt, i) => i === index ? { ...opt, ...updates } : opt));
  };

  const removeGenderOption = (index: number) => {
    setGenderOptions(prev => prev.filter((_, i) => i !== index));
  };

  useEffect(() => {
    fetchZones();
    fetchShopEnabled();
    fetchCertificatesEnabled();
    fetchBookingSettings();
    fetchGenderOptions();
    fetchReminderSettings();
    if (user) {
      setEmail(user.email || "");
      setFullName(user.user_metadata?.full_name || "");
    }
  }, [user]);

  const toggleShop = async (enabled: boolean) => {
    setShopLoading(true);
    setShopEnabled(enabled);
    const { error } = await supabase
      .from("app_settings")
      .upsert({ key: "shop_enabled", value: JSON.stringify(enabled) }, { onConflict: "key" });
    if (error) {
      toast({ title: "Erro ao atualizar", variant: "destructive" });
      setShopEnabled(!enabled);
    } else {
      toast({ title: enabled ? "Loja ativada!" : "Loja desativada!" });
    }
    setShopLoading(false);
  };

  const toggleCertificates = async (enabled: boolean) => {
    setCertificatesLoading(true);
    setCertificatesEnabled(enabled);
    const { error } = await supabase
      .from("app_settings")
      .upsert({ key: "certificates_enabled", value: JSON.stringify(enabled) }, { onConflict: "key" });
    if (error) {
      toast({ title: "Erro ao atualizar", variant: "destructive" });
      setCertificatesEnabled(!enabled);
    } else {
      toast({ title: enabled ? "Certificados ativados!" : "Certificados ocultados!" });
    }
    setCertificatesLoading(false);
  };

  const addZone = async () => {
    if (!newZone.name) return;
    await supabase.from("delivery_zones").insert(newZone);
    setNewZone({ name: "", base_fee: 0 });
    toast({ title: "Zona adicionada!" });
    fetchZones();
  };

  const removeZone = async (id: string) => {
    await supabase.from("delivery_zones").delete().eq("id", id);
    toast({ title: "Zona removida!" });
    fetchZones();
  };

  const startEditZone = (z: Zone) => {
    setEditingZone(z.id);
    setEditValues({ name: z.name, base_fee: z.base_fee });
  };

  const saveEditZone = async () => {
    if (!editingZone) return;
    await supabase.from("delivery_zones").update({ name: editValues.name, base_fee: editValues.base_fee }).eq("id", editingZone);
    setEditingZone(null);
    toast({ title: "Zona atualizada!" });
    fetchZones();
  };

  const handleUpdateName = async () => {
    setSavingProfile(true);
    const { error } = await supabase.auth.updateUser({
      data: { full_name: fullName },
    });
    if (error) {
      toast({ title: "Erro", description: error.message, variant: "destructive" });
    } else {
      // Also update profiles table
      if (user) {
        await supabase.from("profiles").update({ full_name: fullName }).eq("user_id", user.id);
      }
      toast({ title: "Nome atualizado com sucesso!" });
    }
    setSavingProfile(false);
  };

  const handleUpdateEmail = async () => {
    setSavingEmail(true);
    const { error } = await supabase.auth.updateUser({ email });
    if (error) {
      toast({ title: "Erro", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Email atualizado!", description: "Verifique o novo email para confirmar a alteração." });
    }
    setSavingEmail(false);
  };

  const handleUpdatePassword = async () => {
    if (newPassword.length < 6) {
      toast({ title: "Erro", description: "A palavra-passe deve ter pelo menos 6 caracteres.", variant: "destructive" });
      return;
    }
    if (newPassword !== confirmPassword) {
      toast({ title: "Erro", description: "As palavras-passe não coincidem.", variant: "destructive" });
      return;
    }
    setSavingPassword(true);
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) {
      toast({ title: "Erro", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Palavra-passe atualizada com sucesso!" });
      setNewPassword("");
      setConfirmPassword("");
    }
    setSavingPassword(false);
  };

  const runSiteTranslation = async (force: boolean) => {
    setTranslatingSite(true);
    setTranslateStatus("A carregar conteúdos...");

    const { data } = await supabase.from("app_settings").select("key, value");
    const map: Record<string, string> = {};
    (data || []).forEach((row: any) => {
      let val = row.value;
      if (typeof val === "string") { try { val = JSON.parse(val); } catch { /* raw */ } }
      map[row.key] = typeof val === "string" ? val : String(val ?? "");
    });

    const fields = cmsFields
      .filter((f) => f.type === "text" || f.type === "textarea")
      .map((f) => ({ key: f.key, value: map[f.key] || "" }))
      .filter((f) => f.value.trim());

    const res = await translateFields(fields, {
      force,
      existing: map,
      onProgress: (locale, index, total) =>
        setTranslateStatus(`A traduzir ${locale} (${index + 1}/${total})...`),
    });

    setTranslatingSite(false);
    setTranslateStatus(
      res.translatedFields === 0 && !res.failedLocales.length
        ? "Tudo já estava traduzido, nenhum token gasto "
        : `${res.translatedFields} traduções atualizadas · ${res.skipped} reaproveitadas`
    );
    toast({
      title: res.failedLocales.length
        ? `Tradução concluída com falhas (${res.failedLocales.join(", ")})`
        : "Tradução concluída ",
      variant: res.failedLocales.length ? "destructive" : "default",
    });
  };

  return (
    <div>
      <h1 className="font-display text-3xl font-bold mb-8">Definições</h1>

      <div className="max-w-xl space-y-10">
        {/* Translations */}
        <section className="rounded-2xl border border-border bg-card p-6 space-y-3">
          <h2 className="font-display text-xl font-semibold flex items-center gap-2">
            <Languages size={20} className="text-primary" /> Idiomas do Site
          </h2>
          <p className="text-sm text-muted-foreground">
            Traduz para PT-BR, EN, ES, FR e IT apenas os textos que foram alterados desde a última
            tradução, o restante é reaproveitado, sem gastar tokens.
          </p>
          {translateStatus && <p className="text-sm font-medium">{translateStatus}</p>}
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => runSiteTranslation(false)} disabled={translatingSite} className="gap-2 rounded-full">
              <Languages size={16} /> {translatingSite ? "A traduzir..." : "Traduzir site completo"}
            </Button>
            <Button
              variant="outline"
              onClick={() => runSiteTranslation(true)}
              disabled={translatingSite}
              className="gap-2 rounded-full"
            >
              Forçar retradução total
            </Button>
          </div>
        </section>

        {/* Shop Toggle */}
        <section className="rounded-2xl border border-border bg-card p-6 space-y-3">
          <h2 className="font-display text-xl font-semibold flex items-center gap-2">
            <ShoppingBag size={20} className="text-primary" /> Loja Online
          </h2>
          <p className="text-sm text-muted-foreground">
            Quando ativada, a loja aparece no menu principal e os clientes podem ver os produtos.
          </p>
          <div className="flex items-center gap-3">
            <Switch
              checked={shopEnabled}
              onCheckedChange={toggleShop}
              disabled={shopLoading}
            />
            <span className="text-sm font-medium text-foreground">
              {shopEnabled ? "Ativada" : "Desativada"}
            </span>
          </div>
        </section>

        {/* Certificates Toggle */}
        <section className="rounded-2xl border border-border bg-card p-6 space-y-3">
          <h2 className="font-display text-xl font-semibold flex items-center gap-2">
            <Pencil size={20} className="text-primary" /> Página de Certificados
          </h2>
          <p className="text-sm text-muted-foreground">
            Quando ativada, a página "Certificados" aparece no menu, no rodapé e fica acessível aos visitantes.
          </p>
          <div className="flex items-center gap-3">
            <Switch
              checked={certificatesEnabled}
              onCheckedChange={toggleCertificates}
              disabled={certificatesLoading}
            />
            <span className="text-sm font-medium text-foreground">
              {certificatesEnabled ? "Ativada" : "Ocultada"}
            </span>
          </div>
        </section>

        {/* Booking Settings */}
        <section className="rounded-2xl border border-border bg-card p-6 space-y-5">
          <h2 className="font-display text-xl font-semibold flex items-center gap-2">
            <CalendarDays size={20} className="text-primary" /> Gestão de Reservas
          </h2>
          <p className="text-sm text-muted-foreground">
            Configure o tempo de bloqueio antes e depois das reservas e permissões de agendamento.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="bufferBefore" className="flex items-center gap-1.5">
                <Clock size={14} /> Tempo ocupado antes (minutos)
              </Label>
              <Input
                id="bufferBefore"
                type="number"
                min={0}
                step={5}
                value={bufferBefore}
                onChange={(e) => setBufferBefore(parseInt(e.target.value) || 0)}
                placeholder="0"
              />
              <p className="text-xs text-muted-foreground">Minutos bloqueados antes da reserva (ex: preparação)</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="bufferAfter" className="flex items-center gap-1.5">
                <Clock size={14} /> Tempo ocupado depois (minutos)
              </Label>
              <Input
                id="bufferAfter"
                type="number"
                min={0}
                step={5}
                value={bufferAfter}
                onChange={(e) => setBufferAfter(parseInt(e.target.value) || 0)}
                placeholder="0"
              />
              <p className="text-xs text-muted-foreground">Minutos bloqueados depois da reserva (ex: limpeza)</p>
            </div>
          </div>

          <div className="space-y-4 pt-2">
            <div className="flex items-center justify-between rounded-lg border border-border p-4">
              <div className="space-y-0.5">
                <p className="text-sm font-medium text-foreground">Permitir reservas no mesmo dia</p>
                <p className="text-xs text-muted-foreground">
                  Clientes podem agendar para hoje (com antecedência mínima)
                </p>
              </div>
              <Switch checked={allowSameDay} onCheckedChange={setAllowSameDay} />
            </div>

            {allowSameDay && (
              <div className="space-y-2 pl-4 border-l-2 border-primary/20">
                <Label htmlFor="minAdvance">Antecedência mínima (horas)</Label>
                <Input
                  id="minAdvance"
                  type="number"
                  min={1}
                  max={24}
                  value={minAdvanceHours}
                  onChange={(e) => setMinAdvanceHours(parseInt(e.target.value) || 3)}
                  className="w-32"
                />
                <p className="text-xs text-muted-foreground">
                  A reserva deve ser feita com pelo menos {minAdvanceHours}h de antecedência
                </p>
              </div>
            )}
          </div>

          <Button onClick={saveBookingSettings} disabled={savingBookingSettings} className="gap-1.5">
            <Save size={16} /> {savingBookingSettings ? "A guardar..." : "Guardar definições"}
          </Button>
        </section>

        {/* Reminder Automation */}
        <section className="rounded-2xl border border-border bg-card p-6 space-y-5">
          <h2 className="font-display text-xl font-semibold flex items-center gap-2">
            <Bell size={20} className="text-primary" /> Lembretes Automáticos
          </h2>
          <p className="text-sm text-muted-foreground">
            Envio automático de lembretes por email e WhatsApp para o cliente e para o colaborador,
            à hora definida (fuso Europe/Lisbon). Só são enviados para reservas <strong>confirmadas</strong>.
          </p>

          <div className="flex items-center justify-between rounded-xl border border-border p-4">
            <div>
              <Label className="font-semibold">Ativar lembretes automáticos</Label>
              <p className="text-xs text-muted-foreground mt-0.5">Interruptor geral. Se desativado, nada é enviado.</p>
            </div>
            <Switch
              checked={reminder.enabled}
              onCheckedChange={(v) => setReminder({ ...reminder, enabled: v })}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="days_before">Dias antes</Label>
              <Input
                id="days_before"
                value={daysBeforeInput}
                onChange={(e) => setDaysBeforeInput(e.target.value)}
                placeholder="1 ou 1,2,3"
              />
              <p className="text-xs text-muted-foreground">Ex: <code>1</code> envia 1 dia antes. Use vírgulas para vários.</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="send_time">Hora de envio (Lisboa)</Label>
              <Input
                id="send_time"
                type="time"
                value={reminder.send_time}
                onChange={(e) => setReminder({ ...reminder, send_time: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="window">Janela de envio (min)</Label>
              <Input
                id="window"
                type="number"
                min={5}
                max={60}
                value={reminder.window_minutes}
                onChange={(e) => setReminder({ ...reminder, window_minutes: parseInt(e.target.value) || 15 })}
              />
              <p className="text-xs text-muted-foreground">Tolerância à volta da hora de envio.</p>
            </div>
          </div>

          <div className="rounded-xl border border-border p-4 space-y-3">
            <div className="flex items-center justify-between">
              <Label className="font-semibold flex items-center gap-1.5">
                <User size={16} /> Lembrete ao Cliente
              </Label>
              <Switch
                checked={reminder.client.enabled}
                onCheckedChange={(v) => setReminder({ ...reminder, client: { ...reminder.client, enabled: v } })}
              />
            </div>
            {reminder.client.enabled && (
              <>
                <div className="flex flex-wrap gap-4 text-sm">
                  <label className="flex items-center gap-2">
                    <Switch
                      checked={reminder.client.whatsapp}
                      onCheckedChange={(v) => setReminder({ ...reminder, client: { ...reminder.client, whatsapp: v } })}
                    />
                    WhatsApp
                  </label>
                  <label className="flex items-center gap-2">
                    <Switch
                      checked={reminder.client.email}
                      onCheckedChange={(v) => setReminder({ ...reminder, client: { ...reminder.client, email: v } })}
                    />
                    Email
                  </label>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs">Mensagem WhatsApp</Label>
                  <Textarea
                    rows={3}
                    value={reminder.client.message_whatsapp}
                    onChange={(e) => setReminder({ ...reminder, client: { ...reminder.client, message_whatsapp: e.target.value } })}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs">Assunto do email</Label>
                  <Input
                    value={reminder.client.email_subject}
                    onChange={(e) => setReminder({ ...reminder, client: { ...reminder.client, email_subject: e.target.value } })}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs">Corpo do email</Label>
                  <Textarea
                    rows={5}
                    value={reminder.client.email_body}
                    onChange={(e) => setReminder({ ...reminder, client: { ...reminder.client, email_body: e.target.value } })}
                  />
                </div>
              </>
            )}
          </div>

          <div className="rounded-xl border border-border p-4 space-y-3">
            <div className="flex items-center justify-between">
              <Label className="font-semibold flex items-center gap-1.5">
                <Users size={16} /> Lembrete ao Colaborador
              </Label>
              <Switch
                checked={reminder.staff.enabled}
                onCheckedChange={(v) => setReminder({ ...reminder, staff: { ...reminder.staff, enabled: v } })}
              />
            </div>
            {reminder.staff.enabled && (
              <>
                <div className="flex flex-wrap gap-4 text-sm">
                  <label className="flex items-center gap-2">
                    <Switch
                      checked={reminder.staff.whatsapp}
                      onCheckedChange={(v) => setReminder({ ...reminder, staff: { ...reminder.staff, whatsapp: v } })}
                    />
                    WhatsApp
                  </label>
                  <label className="flex items-center gap-2">
                    <Switch
                      checked={reminder.staff.email}
                      onCheckedChange={(v) => setReminder({ ...reminder, staff: { ...reminder.staff, email: v } })}
                    />
                    Email
                  </label>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs">Mensagem WhatsApp</Label>
                  <Textarea
                    rows={3}
                    value={reminder.staff.message_whatsapp}
                    onChange={(e) => setReminder({ ...reminder, staff: { ...reminder.staff, message_whatsapp: e.target.value } })}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs">Assunto do email</Label>
                  <Input
                    value={reminder.staff.email_subject}
                    onChange={(e) => setReminder({ ...reminder, staff: { ...reminder.staff, email_subject: e.target.value } })}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs">Corpo do email</Label>
                  <Textarea
                    rows={5}
                    value={reminder.staff.email_body}
                    onChange={(e) => setReminder({ ...reminder, staff: { ...reminder.staff, email_body: e.target.value } })}
                  />
                </div>
              </>
            )}
          </div>

          <div className="rounded-lg bg-muted/40 p-3 text-xs text-muted-foreground">
            <strong>Variáveis disponíveis:</strong>{" "}
            <code>{"{{nome}}"}</code>, <code>{"{{servico}}"}</code>, <code>{"{{data}}"}</code>,{" "}
            <code>{"{{hora}}"}</code>, <code>{"{{colaborador}}"}</code>, <code>{"{{local}}"}</code>,{" "}
            <code>{"{{telefone}}"}</code>, <code>{"{{email_cliente}}"}</code>, <code>{"{{valor}}"}</code>,{" "}
            <code>{"{{dias}}"}</code>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button onClick={saveReminderSettings} disabled={savingReminder} className="gap-1.5">
              <Save size={16} /> {savingReminder ? "A guardar..." : "Guardar lembretes"}
            </Button>
            <Button variant="outline" onClick={testReminderNow} disabled={testingReminder} className="gap-1.5">
              <Send size={16} /> {testingReminder ? "A testar..." : "Executar agora"}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            O sistema verifica a cada 15 minutos e envia lembretes na hora configurada. Cada reserva
            só recebe o lembrete uma vez por cada "N dias antes".
          </p>
        </section>

        {/* Notificações de Ficha de Avaliação */}
        <section className="rounded-2xl border border-border bg-card p-6 space-y-5">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-xl font-semibold flex items-center gap-2">
              <ClipboardCheck size={20} className="text-primary" /> Notificação de Ficha
            </h2>
          </div>
          <p className="text-sm text-muted-foreground">
            Configure a mensagem enviada ao colaborador via WhatsApp assim que um cliente preenche a ficha de avaliação. A instância de envio segue a configuração em <strong>Integrações / APIs &gt; Roteamento de notificações</strong>.
          </p>
          <div className="space-y-4">
             <div className="rounded-xl border border-border p-4 space-y-4">
              <div className="flex items-center justify-between">
                <Label className="font-semibold flex items-center gap-1.5">
                  <MessageSquare size={16} /> WhatsApp ao Colaborador
                </Label>
                <Switch
                  checked={reminder.staff.whatsapp}
                  onCheckedChange={(v) => setReminder({ ...reminder, staff: { ...reminder.staff, whatsapp: v } })}
                />
              </div>

              <div className="space-y-2">
                <Label className="text-xs">Mensagem WhatsApp</Label>
                <Textarea
                  rows={4}
                  value={reminder.staff.message_whatsapp}
                  onChange={(e) => setReminder({ ...reminder, staff: { ...reminder.staff, message_whatsapp: e.target.value } })}
                  placeholder="Olá {{colaborador}}! Um cliente preencheu a ficha..."
                />
              </div>
            </div>
            <div className="rounded-lg bg-muted/40 p-3 text-xs text-muted-foreground">
              <strong>Variáveis disponíveis:</strong>{" "}
              <code>{"{{colaborador}}"}</code>, <code>{"{{nome}}"}</code>, <code>{"{{telefone}}"}</code>,{" "}
              <code>{"{{email_cliente}}"}</code>, <code>{"{{saude}}"}</code>, <code>{"{{tratamentos}}"}</code>,{" "}
              <code>{"{{pressao}}"}</code>, <code>{"{{aromas}}"}</code>
            </div>
            <Button onClick={saveReminderSettings} disabled={savingReminder} className="gap-1.5">
              <Save size={16} /> {savingReminder ? "A guardar..." : "Guardar configuração"}
            </Button>
          </div>
        </section>



        {/* Gender / Profile Options */}
        <section className="rounded-2xl border border-border bg-card p-6 space-y-5">
          <h2 className="font-display text-xl font-semibold flex items-center gap-2">
            <Users size={20} className="text-primary" /> Perfil do Paciente
          </h2>
           <p className="text-sm text-muted-foreground">
            Configure as opções que aparecem na etapa de seleção de perfil do agendamento.
          </p>

          <div className="flex items-center justify-between rounded-xl border border-border p-4">
            <div>
              <Label className="font-semibold">Mostrar etapa de perfil</Label>
              <p className="text-xs text-muted-foreground mt-0.5">Se desativado, a etapa de perfil do paciente será ocultada do fluxo de agendamento.</p>
            </div>
            <Switch checked={genderStepEnabled} onCheckedChange={setGenderStepEnabled} />
          </div>

          <div className="space-y-3">
            <div className="space-y-2">
              <Label>Título da etapa</Label>
              <Input value={genderStepTitle} onChange={(e) => setGenderStepTitle(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Descrição</Label>
              <Textarea value={genderStepDescription} onChange={(e) => setGenderStepDescription(e.target.value)} rows={3} />
            </div>
          </div>

          <div className="space-y-3">
            <Label>Opções de perfil</Label>
            {genderOptions.map((opt, i) => (
              <div key={i} className="rounded-xl border border-border p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold text-foreground">Opção {i + 1}</span>
                  <Button size="icon" variant="ghost" onClick={() => removeGenderOption(i)} className="h-8 w-8">
                    <Trash2 size={14} />
                  </Button>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Nome</Label>
                    <Input value={opt.label} onChange={(e) => updateGenderOption(i, { label: e.target.value })} placeholder="Ex: Mulher" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Subtítulo (opcional)</Label>
                    <Input value={opt.sublabel || ""} onChange={(e) => updateGenderOption(i, { sublabel: e.target.value || undefined })} placeholder="Ex: (menina)" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Ícone</Label>
                    <Select value={opt.icon} onValueChange={(v) => updateGenderOption(i, { icon: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {availableIcons.map(ic => (
                          <SelectItem key={ic} value={ic}>{ic}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Ação</Label>
                    <Select value={opt.action} onValueChange={(v) => updateGenderOption(i, { action: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="select">Permitir seleção</SelectItem>
                        <SelectItem value="block">Bloquear (mensagem)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">URL da imagem (opcional, substitui o ícone)</Label>
                  <Input value={opt.image_url || ""} onChange={(e) => updateGenderOption(i, { image_url: e.target.value || undefined })} placeholder="https://..." />
                </div>
              </div>
            ))}
            <Button variant="outline" onClick={addGenderOption} className="gap-1.5 w-full">
              <Plus size={16} /> Adicionar opção
            </Button>
          </div>

          <Button onClick={saveGenderOptions} disabled={savingGender} className="gap-1.5">
            <Save size={16} /> {savingGender ? "A guardar..." : "Guardar opções de perfil"}
          </Button>
        </section>
        <section className="rounded-2xl border border-border bg-card p-6 space-y-5">
          <h2 className="font-display text-xl font-semibold flex items-center gap-2">
            <User size={20} className="text-primary" /> Perfil
          </h2>
          <div className="space-y-2">
            <Label htmlFor="fullName">Nome completo</Label>
            <div className="flex gap-2">
              <Input id="fullName" value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder="O seu nome" />
              <Button onClick={handleUpdateName} disabled={savingProfile} className="gap-1 shrink-0">
                <Save size={16} /> {savingProfile ? "A guardar..." : "Guardar"}
              </Button>
            </div>
          </div>
        </section>

        {/* Email Section */}
        <section className="rounded-2xl border border-border bg-card p-6 space-y-5">
          <h2 className="font-display text-xl font-semibold flex items-center gap-2">
            <Mail size={20} className="text-primary" /> Email
          </h2>
          <div className="space-y-2">
            <Label htmlFor="email">Endereço de email</Label>
            <div className="flex gap-2">
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="admin@mentesa.pt" />
              <Button onClick={handleUpdateEmail} disabled={savingEmail} className="gap-1 shrink-0">
                <Save size={16} /> {savingEmail ? "A guardar..." : "Guardar"}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">Ao alterar o email, receberá uma confirmação no novo endereço.</p>
          </div>
        </section>

        {/* Password Section */}
        <section className="rounded-2xl border border-border bg-card p-6 space-y-5">
          <h2 className="font-display text-xl font-semibold flex items-center gap-2">
            <Lock size={20} className="text-primary" /> Palavra-passe
          </h2>
          <div className="space-y-3">
            <div className="space-y-2">
              <Label htmlFor="newPassword">Nova palavra-passe</Label>
              <Input id="newPassword" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="********" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirmar palavra-passe</Label>
              <Input id="confirmPassword" type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="********" />
            </div>
            <Button onClick={handleUpdatePassword} disabled={savingPassword} className="gap-1">
              <Save size={16} /> {savingPassword ? "A guardar..." : "Alterar palavra-passe"}
            </Button>
          </div>
        </section>

        {/* Working Hours - now managed per staff member */}

        {/* Zones Section */}
        <section className="rounded-2xl border border-border bg-card p-6 space-y-5">
          <h2 className="font-display text-xl font-semibold">Zonas de Deslocamento</h2>
          <div className="space-y-2">
            {zones.map((z) => (
              <div key={z.id} className="flex items-center justify-between rounded-lg border border-border p-3">
                {editingZone === z.id ? (
                  <div className="flex gap-2 flex-1 mr-2">
                    <Input value={editValues.name} onChange={(e) => setEditValues({ ...editValues, name: e.target.value })} className="flex-1" />
                    <Input type="number" value={editValues.base_fee} onChange={(e) => setEditValues({ ...editValues, base_fee: parseFloat(e.target.value) || 0 })} className="w-24" />
                    <Button size="sm" onClick={saveEditZone} className="gap-1"><Save size={14} /> Guardar</Button>
                    <Button size="sm" variant="ghost" onClick={() => setEditingZone(null)}>Cancelar</Button>
                  </div>
                ) : (
                  <>
                    <div>
                      <span className="font-medium text-foreground">{z.name}</span>
                      <span className="text-sm text-muted-foreground ml-3">€{Number(z.base_fee).toFixed(2)}</span>
                    </div>
                    <div className="flex gap-1">
                      <Button size="icon" variant="ghost" onClick={() => startEditZone(z)}>
                        <Pencil size={16} />
                      </Button>
                      <Button size="icon" variant="ghost" onClick={() => removeZone(z.id)}>
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            <Input placeholder="Nome da zona" value={newZone.name} onChange={(e) => setNewZone({ ...newZone, name: e.target.value })} />
            <Input type="number" placeholder="Taxa €" className="w-24" value={newZone.base_fee} onChange={(e) => setNewZone({ ...newZone, base_fee: parseFloat(e.target.value) || 0 })} />
            <Button onClick={addZone} className="gap-1"><Plus size={16} /> Adicionar</Button>
          </div>
        </section>

        {/* Data Cleanup Section */}
        <section className="rounded-2xl border border-destructive/30 bg-card p-6 space-y-5">
          <h2 className="font-display text-xl font-semibold flex items-center gap-2 text-destructive">
            <AlertTriangle size={20} /> Limpeza de Dados (Testes)
          </h2>
          <p className="text-sm text-muted-foreground">
            Utilize estas opções para apagar todo o histórico de testes. <strong>Esta ação é irreversível.</strong>
          </p>
          <div className="flex flex-wrap gap-3">
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="destructive" className="gap-1.5" disabled={deletingBookings}>
                  <Trash2 size={16} /> Apagar Todas as Reservas
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Apagar todas as reservas?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Esta ação irá eliminar permanentemente todas as reservas/agendamentos do sistema. Esta operação não pode ser revertida.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                  <AlertDialogAction
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    onClick={async () => {
                      setDeletingBookings(true);
                      const { error } = await supabase.from("bookings").delete().neq("id", "00000000-0000-0000-0000-000000000000");
                      if (error) {
                        toast({ title: "Erro ao apagar reservas", description: error.message, variant: "destructive" });
                      } else {
                        toast({ title: "Todas as reservas foram apagadas!" });
                      }
                      setDeletingBookings(false);
                    }}
                  >
                    Sim, apagar tudo
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>

            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="destructive" className="gap-1.5" disabled={deletingOrders}>
                  <Trash2 size={16} /> Apagar Todas as Vendas
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Apagar todas as vendas/encomendas?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Esta ação irá eliminar permanentemente todas as encomendas e respectivos itens do sistema. Esta operação não pode ser revertida.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                  <AlertDialogAction
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    onClick={async () => {
                      setDeletingOrders(true);
                      // Delete order items first, then orders
                      await supabase.from("order_items").delete().neq("id", "00000000-0000-0000-0000-000000000000");
                      const { error } = await supabase.from("orders").delete().neq("id", "00000000-0000-0000-0000-000000000000");
                      if (error) {
                        toast({ title: "Erro ao apagar vendas", description: error.message, variant: "destructive" });
                      } else {
                        toast({ title: "Todas as vendas foram apagadas!" });
                      }
                      setDeletingOrders(false);
                    }}
                  >
                    Sim, apagar tudo
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </section>
      </div>
    </div>
  );
};

export default AdminSettings;
